<template>
  <div class="upload-section">
    <label :for="inputId" class="upload-label">{{ label }}</label>

    <div class="upload-area" :class="{ 'drag-over': isDragOver, 'has-file': uploadedFile }"
      @dragenter.prevent="handleDragEnter" @dragover.prevent="handleDragOver" @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop" @click="triggerFileInput">
      <input :id="inputId" ref="fileInput" type="file" class="file-input" @change="handleFileSelect" :accept="accept" />

      <div v-if="!uploadedFile" class="upload-placeholder">
        <div class="upload-icon">
          <SvgIcon name="upload-cloud" color="gray" style="width: 48px; height: 48px;" />
        </div>
        <div class="upload-text">
          <p class="upload-primary">Перетащите файл сюда или <span class="upload-link">выберите</span></p>
          <p class="upload-secondary">{{ acceptedFormats }}</p>
        </div>
      </div>

      <div v-else class="uploaded-file">
        <div class="file-info">
          <div class="file-icon" :class="getFileTypeClass(uploadedFile.name)">
            <SvgIcon v-if="uploadedFile.name.endsWith('.pdf')" name="file-icon"
              style="width: 24px; height: 24px;" />
            <SvgIcon v-else name="file-default-icon" style="width: 24px; height: 24px;" />
          </div>
          <div class="file-details">
            <p class="file-name">{{ uploadedFile.name }}</p>
          </div>
        </div>
        <button type="button" class="file-remove" @click.stop="removeFile" title="Удалить файл">
          <q-icon name="delete" color="red" style="font-size: 20px;" />
        </button>
      </div>
    </div>

    <div v-if="fileError" class="file-error">
      {{ fileError }}
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/common/SvgIcon';

export default {
  name: "DocumentUploader",
  components: { SvgIcon },
  props: {
    label: {
      type: String,
      default: 'Загрузить документ'
    },
    accept: {
      type: String,
      default: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar'
    },
    acceptedFormats: {
      type: String,
      default: 'PDF'
    },
    allowedExtensions: {
      type: Array,
      default: () => ['.pdf']
    }
  },
  emits: ['file-selected', 'file-removed', 'file-error'],
  data() {
    return {
      uploadedFile: null,
      isDragOver: false,
      fileError: '',
      inputId: `file-input-${Math.random().toString(36).substr(2, 9)}`,
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.validateAndSetFile(file)
      }
    },
    handleDragEnter() {
      this.isDragOver = true
    },
    handleDragOver() {
      this.isDragOver = true
    },
    handleDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOver = false
      }
    },
    handleDrop(event) {
      this.isDragOver = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.validateAndSetFile(file)
      }
    },
    validateAndSetFile(file) {
      this.fileError = ''

      const fileName = file.name.toLowerCase()
      const isValidType = this.allowedExtensions.some(ext => fileName.endsWith(ext))

      if (!isValidType) {
        const formats = this.allowedExtensions.join(', ').toUpperCase()
        this.fileError = `Недопустимый формат файла. Разрешены: ${formats}`
        this.$emit('file-error', this.fileError)
        return
      }

      this.uploadedFile = file
      this.$emit('file-selected', file)
    },
    removeFile() {
      this.uploadedFile = null
      this.fileError = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.$emit('file-removed')
    },
    getFileTypeClass(fileName) {
      const name = fileName.toLowerCase()
      if (name.endsWith('.pdf')) return 'file-pdf'
      if (name.endsWith('.doc') || name.endsWith('.docx')) return 'file-doc'
      if (name.endsWith('.xls') || name.endsWith('.xlsx')) return 'file-xls'
      if (name.endsWith('.zip') || name.endsWith('.rar')) return 'file-zip'
      return 'file-default'
    },
    getFile() {
      return this.uploadedFile
    },
    clearFile() {
      this.removeFile()
    },
    setError(error) {
      this.fileError = error
    }
  }
}
</script>

<style scoped lang="scss">
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 5px;
}

.upload-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.upload-area {
  position: relative;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px 24px;
  background-color: #f9fafb;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }

  &.drag-over {
    border-color: #2563eb;
    background-color: #dbeafe;
    transform: scale(1.02);
  }

  &.has-file {
    padding: 20px;
    min-height: auto;
    border-style: solid;
    border-color: #10b981;
    background-color: #f0fdf4;

    &:hover {
      background-color: #ecfdf5;
    }
  }
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  width: 100%;
}

.upload-icon {
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  .upload-area:hover & {
    color: #3b82f6;
    transform: translateY(-4px);
  }

  .upload-area.drag-over & {
    color: #2563eb;
    transform: scale(1.1);
  }
}

.upload-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upload-primary {
  font-size: 15px;
  color: #374151;
  font-weight: 500;
  margin: 0;
}

.upload-link {
  color: #3b82f6;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #2563eb;
  }
}

.upload-secondary {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.uploaded-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex-shrink: 0;

  &.file-pdf {
    background-color: #fee2e2;
    color: #dc2626;
  }

  &.file-doc {
    background-color: #dbeafe;
    color: #2563eb;
  }

  &.file-xls {
    background-color: #d1fae5;
    color: #059669;
  }

  &.file-zip {
    background-color: #fef3c7;
    color: #d97706;
  }

  &.file-default {
    background-color: #e5e7eb;
    color: #6b7280;
  }
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.file-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background-color: #fee2e2;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: #fecaca;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

.file-error {
  font-size: 13px;
  color: #dc2626;
  padding: 8px 12px;
  background-color: #fee2e2;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 428px) {
  .upload-area {
    padding: 24px 16px;
    min-height: 120px;
  }

  .upload-icon svg {
    width: 40px;
    height: 40px;
  }

  .upload-primary {
    font-size: 14px;
  }

  .upload-secondary {
    font-size: 12px;
  }
}
</style>

